import React, { useEffect } from "react";

import {
  ColumnDef,
  PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "./pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTableToolbar } from "./data-table-toolbar";

export interface DataTableBaseProps {
  handleChange: (
    index: number,
    filterText?: string,
    options?: { silentRefresh?: boolean }
  ) => void;
  isLoading: boolean;
  pageCount: number;
}

interface DataTableProps<TData, TValue> extends DataTableBaseProps {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

export function DataTable<TData, TValue>({
  columns,
  data,
  handleChange,
  pageCount,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  useEffect(() => {
    handleChange(pageIndex);

    const timer = setInterval(() => {
      handleChange(pageIndex, "", { silentRefresh: true });
    }, 30000);

    return () => {
      clearInterval(timer);
    };
  }, [pageIndex]);

  const handleRefresh = () => {
    handleChange(pageIndex);
  };

  const OnFilterChange = (filterText: string) => {
    handleChange(pageIndex, filterText, { silentRefresh: true });
  };

  return (
    <div className="space-y-4">
      <DataTableToolbar
        onRefresh={handleRefresh}
        onFilterChange={OnFilterChange}
      />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <>
                {!isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <Skeleton className="w-full h-6" />
                      <Skeleton className="w-full h-6 mt-3" />
                      <Skeleton className="w-full h-6 mt-3" />
                      <Skeleton className="w-full h-6 mt-3" />
                      <Skeleton className="w-full h-6 mt-3" />
                      <Skeleton className="w-full h-6 mt-3" />
                      <Skeleton className="w-full h-6 mt-3" />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 flex justify-end">
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
